'use client';

import React from 'react';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import { Routes } from '@/routes';
import {
  IContactInformation,
  IDescriptionsResort,
  IFirstHandReport,
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IResortReviews,
  IResortStrengthsOverview,
  IResortTrailmapPage,
  IReviewsGeneral,
  ISnowfallItem,
} from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import PlanYourTrip from '@/components/common/PlanYourTrip/ResortWidget';
import { TerrainOverview } from '@/components/resort/TerrainDetailed';
import { TotalLifts } from '@/components/resort/TotalLifts';
import { Hero400Error } from '@/components/400';
import { useSanitizedHtmlProps } from '@/util/sanitized';
import { ResortPhotosData } from '@/components/widget/types';
import { INearbyResort } from '@/api/region/types';
import { OverviewTopSlider } from '@/components/resort/OverviewTopSlider';
import { ImagesSection } from '@/components/resort/ImagesSection';
import { HowToGetThereSection } from '@/components/resort/HowToGetThereSection';
import { WhenToGoSection } from '@/components/resort/WhenToGoSection';
import { InsideScoopSection } from '@/components/resort/InsideScoopSection';
import { ReviewsSection } from '@/components/resort/ReviewsSection';
import { NearbyResorts } from '@/components/resort/NearbyResorts';
import { useIsPermanentlyClosed } from '@/util/useIsPermanentlyClosed';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { PageBody } from '@/components/common/PageBody';
import { AtAGlanceCard } from '@/components/resort/AtAGlanceCard';
import { SnowfallCard, type SnowfallMonth } from '@/components/resort/SnowfallCard';
import { DistanceUnit, SnowUnit } from '@/components/blocks/Unit';
import { formatDateFromStr, SHORT_DATE_FORMAT } from '@/util/formatDate';
import { DateTime } from 'luxon';
import { cn } from '@/util/cn';

const filterOutTabData = (data?: {
  dangerouslySetInnerHTML: {
    __html: string;
  };
}) => {
  if (!data) {
    return data;
  }
  const FORBIDDEN_CHARACTERS = '►';
  const result = data;

  // eslint-disable-next-line no-underscore-dangle
  let html = data.dangerouslySetInnerHTML.__html;
  const forbiddenCharacters = FORBIDDEN_CHARACTERS.split('');
  forbiddenCharacters.forEach((char) => {
    html = html.replace(new RegExp((char), 'g'), '');
  });
  // eslint-disable-next-line no-underscore-dangle
  result.dangerouslySetInnerHTML.__html = html;
  return result;
};

export type ResortOverviewPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  resortStrengths: IResortStrengthsOverview,
  resortDescriptionTabs: Array<IDescriptionsResort>,
  snowfallInfoAnnual: Array<ISnowfallItem> | null,
  snowfallInfoMonthly: Array<ISnowfallItem> | null,
  trailmap: IResortTrailmapPage,
  contactInformation: IContactInformation,
  reviewsGeneral: IReviewsGeneral | null
  relatedNews: IRelatedNews | null,
  initials: {
    firstHand: IFirstHandReport | null,
  },
  photos: ResortPhotosData,
  nearbyResorts: INearbyResort[],
  resortReviews: IResortReviews
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-overview-page';
export const TEMPLATE_ID = '3.2.1';
export const TEMPLATE_NAME = 'Resort Overview Resort';

export default function ResortOverviewPage({
  fullResort,
  pathInfo,
  resortDescriptionTabs,
  trailmap,
  contactInformation,
  photos,
  nearbyResorts,
  snowfallInfoAnnual,
  snowfallInfoMonthly,
  resortReviews,
  relatedNews,
}: ResortOverviewPageProps) {
  const { t } = useOTSTranslation('common');
  const resortCountry = fullResort?.region?.country_code;

  const featuredImage = fullResort.image
    ? [{ cdnUrl: fullResort.image, name: fullResort.title }]
    : [];

  const topSliderPhotos = [...featuredImage, ...photos];

  const tabs = (resortDescriptionTabs.reduce((prev: Record<string, string>, curr) => ({ ...prev, [curr.tab]: (curr.description || '') }), {}));

  const resortDescription = useSanitizedHtmlProps(tabs.resort_profile ?? '');

  const transportationDescription = useSanitizedHtmlProps(tabs.transportation_profile ?? '');

  const mountainOverview = useSanitizedHtmlProps(tabs.mountain_overview ?? '');

  const insideScoop = useSanitizedHtmlProps(tabs.inside_scoop ?? '');

  const { city } = contactInformation.address;
  const { country } = contactInformation.address;

  const totalSnowfall = snowfallInfoAnnual?.reduce((prev, curr) => prev + curr.totalSnow, 0);

  const annualSnowfall = totalSnowfall && snowfallInfoAnnual && Math.ceil(totalSnowfall / snowfallInfoAnnual?.length);

  const totalLifts = trailmap
    ? Object.values(trailmap.lifts).reduce((prev, value) => prev + value, 0)
    : 0;

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const isPermanentlyClosed = useIsPermanentlyClosed(fullResort);
  const isShowingImagesSection = photos && photos.length > 2;

  return (
    <>
      <Main>
        <div className="relative overflow-clip xl:mt-4">

          <div className="flex flex-col-reverse max-md:mt-[39px] md:flex-row-reverse">
            {trailmap && fullResort.forecast && (
              <div className="flex min-h-0 w-full flex-col items-stretch md:w-[360px] md:shrink-0">
                <div
                  className={cn(
                    'box-border flex w-full flex-col items-stretch gap-6 self-start pl-[19px]',
                    'max-md:gap-4 max-md:pl-0',
                  )}
                >
                  <AtAGlanceCard
                    title={fullResort.title}
                    stats={[
                      ...(trailmap.dates?.projectedOpening
                        ? [{
                          label: t('resort.skiReport.projectedOpening'),
                          value: formatDateFromStr(trailmap.dates.projectedOpening, SHORT_DATE_FORMAT),
                        }]
                        : []),
                      ...(trailmap.dates?.projectedClosing
                        ? [{
                          label: t('resort.skiReport.projectedClosing'),
                          value: formatDateFromStr(trailmap.dates.projectedClosing, SHORT_DATE_FORMAT),
                        }]
                        : []),
                      ...(totalLifts ? [{ label: t('resort.overview.lifts'), value: totalLifts }] : []),
                      ...(trailmap.runs?.total ? [{ label: t('region.skiReport.trails-abb'), value: trailmap.runs.total }] : []),
                      ...(trailmap.runs?.skiableAc
                        ? [{
                          label: t('resort.overview.skiableTerrain'),
                          value: <DistanceUnit values={trailmap.runs.skiableAc} type="acOrHa" formatToLocale />,
                        }]
                        : []),
                      ...(trailmap.elevation?.base
                        ? [{
                          label: t('common.base'),
                          value: <DistanceUnit values={trailmap.elevation.base} type="mOrFeetShort" formatToLocale />,
                        }]
                        : []),
                      ...(trailmap.elevation?.summit
                        ? [{
                          label: t('common.summit'),
                          value: <DistanceUnit values={trailmap.elevation.summit} type="mOrFeetShort" formatToLocale />,
                        }]
                        : []),
                      ...(trailmap.elevation?.verticalDrop
                        ? [{
                          label: t('resort.overview.verticalDrop'),
                          value: <DistanceUnit values={trailmap.elevation.verticalDrop} type="mOrFeetShort" formatToLocale />,
                        }]
                        : []),
                      ...(annualSnowfall
                        ? [{
                          label: t('resort.overview.averageSnowfall'),
                          value: <SnowUnit values={annualSnowfall} />,
                        }]
                        : []),
                    ]}
                  />
                  {!!annualSnowfall && !!snowfallInfoMonthly?.length && (
                    <SnowfallCard
                      title={t('region.skiReport.snowfall')}
                      annualValue={<SnowUnit values={annualSnowfall} />}
                      annualLabel={t('resort.overview.annualSnowfall')}
                      monthly={snowfallInfoMonthly.slice(0, 6).map<SnowfallMonth>((item) => ({
                        month: DateTime.fromJSDate(new Date(item.date)).monthShort ?? '',
                        value: item.totalSnow,
                        display: <SnowUnit values={item.totalSnow} />,
                      }))}
                      href={Routes.resortSnowfall(pathInfo)}
                      ariaLabel={t('resort.overview.annualSnowfall')}
                    />
                  )}
                  <RelatedArticles
                    relatedNewsList={relatedNews}
                  />
                  <SidebarAd id={pathInfo.uuid} />
                </div>
              </div>
            )}
            <section className="min-w-0 flex-1">
              <PageBody>
                {photos && resortDescription && (
                  <OverviewTopSlider
                    photos={topSliderPhotos}
                    resortDescription={filterOutTabData(resortDescription)}
                  />
                )}

                {isShowingImagesSection && (
                  <ImagesSection photos={photos.slice(1)} />
                )}

                <SkiGptPromotion
                  templateId={TEMPLATE_ID}
                  name={fullResort.title}
                />
                <PlanYourTrip
                  resort={pathInfo}
                  hasSkiRental={pathInfo.hasSkiRental}
                  hasFlights={pathInfo.hasFlights}
                  hasCarRental={pathInfo.hasCarRental}
                  liftsUrl={fullResort.liftsUrl}
                  rentalUrl={fullResort.rentalUrl}
                  lessonsUrl={fullResort.lessonsUrl}
                  isPartner={fullResort.isPartner}
                  resortCountry={resortCountry}
                />

                {transportationDescription && contactInformation && (
                  <HowToGetThereSection
                    city={city}
                    contactInformation={contactInformation}
                    country={country}
                    description={filterOutTabData(transportationDescription)}
                    fullResort={fullResort}
                  />
                )}

                {trailmap && trailmap.dates && (
                  <WhenToGoSection
                    mountainOverview={mountainOverview}
                    trailmap={trailmap}
                    isPermanentlyClosed={isPermanentlyClosed}
                    annualSnowfall={annualSnowfall ?? undefined}
                  />
                )}
                {trailmap && (
                  <TerrainOverview
                    data={trailmap}
                    pathInfo={pathInfo}
                    resortCountry={resortCountry}
                  />
                )}
                {trailmap && <TotalLifts lifts={trailmap.lifts} />}

                {/* eslint-disable-next-line no-underscore-dangle */}
                {insideScoop.dangerouslySetInnerHTML.__html && (
                  <InsideScoopSection paragraphs={filterOutTabData(insideScoop)} photos={photos.slice(4)} />
                )}

                {resortReviews.pagination.count > 0 && (
                  <ReviewsSection pathInfo={pathInfo} resortReviews={resortReviews} />
                )}

                {nearbyResorts
                  && <NearbyResorts nearbyResorts={nearbyResorts.slice(1)} />}
              </PageBody>
            </section>

          </div>
        </div>

      </Main>
    </>
  );
}

// fetchProps moved to ./ski-resort.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
